const ATTRIBUTION_VERSION = 1;
const DEFAULT_ATTRIBUTION_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

type AttributionEntry = {
  id: string;
  ts: number;
};

type AttributionStore = {
  v: number;
  sources: Record<string, AttributionEntry[]>;
};

const getAttributionKey = (storeId: string) => `pako:attribution:${storeId}`;

const normalizeStore = (value: unknown): AttributionStore => {
  if (!value || typeof value !== "object") {
    return { v: ATTRIBUTION_VERSION, sources: {} };
  }

  const raw = value as { v?: unknown; sources?: unknown };
  const sources: Record<string, AttributionEntry[]> = {};

  if (raw.sources && typeof raw.sources === "object") {
    for (const [source, list] of Object.entries(raw.sources as Record<string, unknown>)) {
      if (!Array.isArray(list)) continue;
      const entries = list
        .map((entry) => {
          const e = entry as { id?: unknown; ts?: unknown };
          const id = typeof e?.id === "string" ? e.id : null;
          const ts = typeof e?.ts === "number" ? e.ts : null;
          if (!id || ts == null) return null;
          return { id, ts };
        })
        .filter((entry): entry is AttributionEntry => Boolean(entry));
      if (entries.length > 0) sources[source] = entries;
    }
  }

  return {
    v: typeof raw.v === "number" ? raw.v : ATTRIBUTION_VERSION,
    sources,
  };
};

const pruneStore = (store: AttributionStore, now: number, ttlMs: number): AttributionStore => {
  const pruned: AttributionStore = { v: ATTRIBUTION_VERSION, sources: {} };
  for (const [source, entries] of Object.entries(store.sources)) {
    const next = entries.filter((entry) => (now - entry.ts) < ttlMs);
    if (next.length > 0) pruned.sources[source] = next;
  }
  return pruned;
};

export const storeAttribution = ({
  storeId,
  source,
  productId,
  ttlMs = DEFAULT_ATTRIBUTION_TTL_MS,
}: {
  storeId: string;
  source: string;
  productId: string | number | undefined | null;
  ttlMs?: number;
}) => {
  if (!storeId || !source || productId == null) return;

  const id = String(productId);
  const key = getAttributionKey(storeId);
  const now = Date.now();

  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? normalizeStore(JSON.parse(raw)) : normalizeStore(null);
    const pruned = pruneStore(parsed, now, ttlMs);
    const list = pruned.sources[source] ? pruned.sources[source].slice() : [];
    const without = list.filter((entry) => entry.id !== id);
    without.push({ id, ts: now });
    pruned.sources[source] = without;
    localStorage.setItem(key, JSON.stringify(pruned));
  } catch {
    // Ignore storage errors; tracking must not break UX
  }
};

